// @ts-nocheck
import { createContext, useContext, useMemo } from 'react';

/**
 * V1 scope gating for the shared sequence builder (PRD v1.3 §8.4.7).
 *
 * Every flag DEFAULTS to `true`, which means "current behavior" — i.e. the
 * recall app, which does not pass `v1Scope`, is completely unaffected. The
 * email-sms app passes the V1-narrowed flags (all `false`) to constrain the
 * builder to V1 scope.
 *
 * When a flag is `false`, the corresponding feature is narrowed:
 *  - scheduleTimer     → hide the Schedule sub-toggle + SCHEDULED DATE/TIME; Wait only.
 *  - checkConditionMode→ "Check" condition mode disabled / "coming soon"; Branch only.
 *  - andCondition      → "Add AND Condition" disabled / "coming soon".
 *  - smsConditionBinding → email-open condition cannot bind to an SMS step.
 *  - nestedConditions  → no condition inside a branch path (single-level branching).
 *  - entryNodeEditable → entry/trigger node is display-only.
 *  - aiChat            → AI chat sidebars + "Add by AI" affordances hidden.
 */
export interface V1Scope {
  scheduleTimer?: boolean;
  checkConditionMode?: boolean;
  andCondition?: boolean;
  smsConditionBinding?: boolean;
  nestedConditions?: boolean;
  entryNodeEditable?: boolean;
  aiChat?: boolean;
}

/** Fully-defaulted scope where every flag is `true` (= current behavior). */
export type ResolvedV1Scope = Required<V1Scope>;

const DEFAULT_V1_SCOPE: ResolvedV1Scope = {
  scheduleTimer: true,
  checkConditionMode: true,
  andCondition: true,
  smsConditionBinding: true,
  nestedConditions: true,
  entryNodeEditable: true,
  aiChat: true,
};

const V1ScopeContext = createContext<ResolvedV1Scope>(DEFAULT_V1_SCOPE);

export function V1ScopeProvider({
  value,
  children,
}: {
  value?: V1Scope;
  children: React.ReactNode;
}) {
  const resolved = useMemo<ResolvedV1Scope>(
    () => ({
      scheduleTimer: value?.scheduleTimer ?? true,
      checkConditionMode: value?.checkConditionMode ?? true,
      andCondition: value?.andCondition ?? true,
      smsConditionBinding: value?.smsConditionBinding ?? true,
      nestedConditions: value?.nestedConditions ?? true,
      entryNodeEditable: value?.entryNodeEditable ?? true,
      aiChat: value?.aiChat ?? true,
    }),
    [
      value?.scheduleTimer,
      value?.checkConditionMode,
      value?.andCondition,
      value?.smsConditionBinding,
      value?.nestedConditions,
      value?.entryNodeEditable,
      value?.aiChat,
    ],
  );

  return <V1ScopeContext.Provider value={resolved}>{children}</V1ScopeContext.Provider>;
}

/** Read the resolved V1 scope. Defaults to all-`true` (current behavior). */
export function useV1Scope(): ResolvedV1Scope {
  return useContext(V1ScopeContext);
}

/**
 * Shared action-type list used by binding checks. An email-open condition may
 * only bind to an email step (PRD §8.4.4 / §8.4.7 row 4).
 */
export const SMS_ACTION_TYPES = ['action-send-sms'] as const;
export const EMAIL_ACTION_TYPES = ['action-send-email'] as const;

/**
 * Conditions that are "email-open" semantics and must bind to an email step.
 * Used by the smsConditionBinding gate.
 */
export const EMAIL_BOUND_CONDITION_TYPES = [
  'condition-email-opened',
  'condition-email-clicked',
] as const;
